import {
  BoardPositions,
  InnerBoardBoxType,
  OuterBoardBoxType,
  OuterBoardPositions,
} from "./board";
import { DataClass } from "./data";
import { ExpensesConstant, GameStates, LiabilityConstant } from "./game";

export const CommonConstant = {
  emailRegex:
    /^(([^<>()[\]\\.,;:\s@\\"]+(\.[^<>()[\]\\.,;:\s@\\"]+)*)|(\\".+\\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
};

export const {
  REACT_APP_FIREBASE_API_KEY,
  REACT_APP_FIREBASE_AUTH_DOMAIN,
  REACT_APP_FIREBASE_PROJECT_ID,
  REACT_APP_FIREBASE_STORAGE_BUCKET,
  REACT_APP_FIREBASE_MSG_SENDER_ID,
  REACT_APP_FIREBASE_APP_ID,
  REACT_APP_FIREBASE_MEASUREMENT_ID,
  REACT_APP_FIREBASE_DB_URL,
} = process.env;

export const ColorsConstant = [
  "#ffbe0b",
  "#fb5607",
  "#ff006e",
  "#8338ec",
  "#3a86ff",
  "#073b4c",
  "#00ff7f", //extra added color Spring Green
  "#8a2be2", //extra added color Blue Violet
  "#ff4500", //extra added color Orange Red
  "#00ced1", //extra added color Dark Turquoise
];

export const playAudio = async (url, player) => {
  if(player && player?.muteAudio){   
    return
  }
  const response = await fetch(url, {
    headers: {
      Range: "bytes=0-",
    },
  });

  if (response.status === 206) {
    const contentType = response.headers.get("Content-Type");
    if (contentType && contentType.startsWith("audio/")) {
      const blob = await response.blob();
      const audioUrl = URL.createObjectURL(blob);
      const audioPlayer = new Audio(audioUrl);
      audioPlayer.play();
    } else {
      console.error("Unexpected content type:", contentType);
    }
  } else {
    console.error("Failed to fetch audio. Status:", response.status);
  }
};

export const randomIndex = (length) => Math.floor(Math.random() * length);

export const calculateCash = (player) => {
  if (player.fastTrack) {
    const fastTrackAssetsIncome = (player.fastTrackAssets || []).reduce(
      (a, b) => a + (b.cashFlow || 0),
      0,
    );
    let passiveIncome = player?.initialCashFlowDay + fastTrackAssetsIncome;
    let cashFlowDay = player?.initialCashFlowDay  + fastTrackAssetsIncome;
    if(passiveIncome === 0 && player?.passiveIncome > 0 ){
      passiveIncome =  player?.passiveIncome
    }
    
    return {
      passiveIncome : passiveIncome || player?.passiveIncome,
      cashFlowDay :  cashFlowDay || player?.cashFlowDay
    };
  }
  const totalExpenses = (player.expenses || []).reduce(
    (a, b) => a + b.value,
    0,
  );
  const dividend = (player.stockAssets || []).reduce(
    (a, b) => a + b.shares * (b.dividend || 0),
    0,
  );
  const assetsIncome = (player.realEstateAssets || []).reduce(
    (a, b) => a + (b.cashFlow || 0),
    0,
  );
  const passiveIncome = dividend + assetsIncome;
  const totalIncome = passiveIncome + player.salary;
  const payday = totalIncome - totalExpenses;

  return {
    totalExpenses,
    totalIncome,
    passiveIncome,
    payday,
  };
};

export const adjustNewLiability = (liabilities, newLiability) => {
  if ((liabilities || []).some((x) => x.key === newLiability.key)) {
    return liabilities.map((item) => {
      if (item.key === newLiability.key) {
        return {
          ...item,
          value: item.value + newLiability.value,
        };
      }
      return item;
    });
  } else {
    return [...(liabilities || []), newLiability];
  }
};

export const adjustNewExpense = (expenses, newExpense) => {
  if ((expenses || []).some((x) => x.key === newExpense.key)) {
    return expenses.map((item) => {
      if (item.key === newExpense.key) {
        return {
          ...item,
          value: item.value + newExpense.value,
        };
      }
      return item;
    });
  } else {
    return [...(expenses || []), newExpense];
  }
};

const AlwaysShowExpenseKeys = [
  "schoolLoan",
  "carPayment",
  "creditCardPayment",
  "retailPayment",
];

export const generateExpenses = (currentScenario) => {
  const expenses = [];
  Object.keys(ExpensesConstant).forEach((key) => {
    if (key === "children") return;
    if (key === "loans") return;
    const scenarioField = key === "schoolLoan" ? "loans" : key;
    const value = currentScenario[scenarioField] || 0;
    if (value || AlwaysShowExpenseKeys.includes(key)) {
      expenses.push({
        name: ExpensesConstant[key],
        key,
        value,
      });
    }
  });
  return [...expenses];
};

export const generateNewPlayer = (user, totalPlayer) => {
  const scenarios = Object.keys(DataClass.scenarios);

  const scenarioKeyIndex = randomIndex(scenarios.length);
  const scenarioKey = Object.keys(DataClass.scenarios)[scenarioKeyIndex];
  const currentScenario = DataClass.scenarios[scenarioKey];

  const temp = {
    id: user.uid,
    name: user.displayName || "Guest",
    position: 1,
    assetIncome: 0,
    cash: currentScenario?.startingSavings,
    totalIncome: 0,
    salary: currentScenario?.startingSalary,
    cashFlowDay: 0,
    color: ColorsConstant[totalPlayer],
    dream: "",
    jobTitle: currentScenario?.jobTitle,
    expenses: [],
    realEstateAssets: [],
    stockAssets: [],
    personalAssets: [],
    passiveIncome: 0,
    totalExpenses: 0,
    payday: 0,
    downsizedTurns: 0,
    charityTurns: 0,
    children: 0,
    perChildCost: currentScenario?.children || 0,
  };

  temp.expenses = generateExpenses(currentScenario);

  const AlwaysShowLiabilityKeys = ["schoolLoan", "carLoan", "creditDebt", "retailDebt"];
  const liabilities = [];
  Object.keys(LiabilityConstant).forEach((key) => {
    if (key === "loans") return;
    const value = currentScenario[key] || 0;
    if (value || AlwaysShowLiabilityKeys.includes(key)) {
      liabilities.push({
        id: Math.random(),
        name: LiabilityConstant[key],
        key,
        value,
      });
    }
  });
  temp.liabilities = [...liabilities];
  const calculatedFields = calculateCash(temp);

  return {
    ...temp,
    ...calculatedFields,
  };
};

export const shuffleArray = (input) => {
  const arr = [...input];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};

const DeckSources = {
  doodad: () => DataClass.cards.doodad,
  bigDeal: () => DataClass.cards.bigDeal,
  smallDeal: () => DataClass.cards.smallDeal,
  offer: () => DataClass.cards.offer,
};

export const createShuffledDecks = () => {
  const decks = {};
  Object.entries(DeckSources).forEach(([name, getSource]) => {
    const source = getSource() || {};
    decks[name] = {
      order: shuffleArray(Object.keys(source)),
      index: 0,
    };
  });
  return decks;
};

export const getCardFromDeck = (deckName, deck) => {
  const source = DeckSources[deckName]?.() || {};
  if (!deck || !deck.order?.length) return null;
  const safeIndex = ((deck.index || 0) % deck.order.length + deck.order.length) % deck.order.length;
  const key = deck.order[safeIndex];
  return source[key] || null;
};

export const rollDie = (dieCount) => {
  const dice = [];
  for (let i = 1; i <= dieCount; i += 1) {
    dice.push(randomIndex(6) + 1);
  }
  const total = dice.reduce((sum, value) => sum + value, 0);

  return { dice, total };
};

export const getInnerGameState = (position) => {
  const currentBoard = BoardPositions.find((_, index) => index === position);
  switch (currentBoard?.type) {
    case InnerBoardBoxType.deal:
      return GameStates.deal;

    case InnerBoardBoxType.charity:
      return GameStates.charity;

    case InnerBoardBoxType.baby:
      return GameStates.baby;

    case InnerBoardBoxType.doodad:
      return GameStates.doodad;

    case InnerBoardBoxType.market:
      return GameStates.market;

    case InnerBoardBoxType.payday:
      return GameStates.finishTurn;

    case InnerBoardBoxType.downsized:
      return GameStates.downsized;

    default:
      return "";
  }
};

export const getFastTrackGameState = (position) => {
  const currentBoard = OuterBoardPositions.find(
    (_, index) => index === position,
  );
  switch (currentBoard?.type) {
    case OuterBoardBoxType.opportunity:
    case OuterBoardBoxType.opportunity1:
      return GameStates.opportunity;

    case OuterBoardBoxType.charity:
      return GameStates.charity;

    case OuterBoardBoxType.doodad:
      return GameStates.ftDoodad;

    case OuterBoardBoxType.cashflow:
      return GameStates.cashflow;

    default:
      return "";
  }
};

export const isStockSplitDeal = (deal) =>
  !!(deal && (deal.stockSplit || deal.reverseSplit));

export const getStockSplitFactor = (deal) => {
  if (!deal) return 1;
  if (deal.splitRatio) {
    const parts = String(deal.splitRatio).split(":").map((s) => parseFloat(s.trim()));
    if (parts.length === 2 && parts[0] > 0 && parts[1] > 0) {
      return parts[0] / parts[1];
    }
  }
  if (deal.stockSplit) return 2;
  if (deal.reverseSplit) return 0.5;
  return 1;
};

export const getRandomCashflow = () => {
  const cashflows = [1000, 2000, 3000, 4000, 5000, 6000, 9500, 7500];
  const index = randomIndex(cashflows.length);
  return cashflows[index];
};

export const isNotEmpty = (item) => {
  return (
    item !== undefined && item !== null && item !== "" && item.length !== 0
  );
};

export const currencyFormat = (value, currency) => {
  if (Number.isNaN(value || 0)) {
    return value;
  }

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency || "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.round(value || 0));
};

export const numberWithCommas = (x) => {
  return x?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

export const roundNumber = (num, decimals = 2) => {
  if (!isNotEmpty(num)) {
    return "";
  }
  const t = 10 ** decimals;
  let result = Math.round((num + Number.EPSILON) * t) / t;
  if (num < 0) {
    result *= -1;
  }
  return result;
};

export const createKeyPressHandler = (regex) => (event) => {
  if (!regex.test(event.key)) {
    event.preventDefault();
  }
};

export const digit = (value) => {
  const leftDigit = value >= 10 ? value.toString()[0] : "0";
  const rightDigit = value >= 10 ? value.toString()[1] : value.toString();
  return `${leftDigit}${rightDigit}`;
};
